import {
  $applyNodeReplacement,
  $createLineBreakNode,
  $createTextNode,
  $getRoot,
  $isElementNode,
  $isLineBreakNode,
  $isTextNode,
  DecoratorNode,
  ElementNode,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalEditor,
  type LexicalNode,
  type NodeKey,
  type RangeSelection,
  type SerializedElementNode,
  type SerializedLexicalNode,
} from "lexical";
import {
  escapeSignatureHtml,
  normalizeSignatureEditorHtml,
  signatureEditorFragment,
  signatureEditorStyle,
} from "./signature-editor-policy";

export interface SignatureEditorLogo {
  id: string;
  content_id: string;
  preview_url: string;
  width: number;
  height: number;
}

type Attributes = Record<string, string>;
type SerializedSignatureElement = SerializedElementNode & { tag: string; attrs: Attributes };
type SerializedSignatureMedia = SerializedLexicalNode & { tag: "img" | "hr"; attrs: Attributes; previewUrl: string };

function applyAttributes(dom: HTMLElement, attrs: Attributes): void {
  for (const [name, value] of Object.entries(attrs)) dom.setAttribute(name, value);
}

/**
 * A signature layout node retains its safe HTML tag and attributes. Using a generic email
 * element avoids a table exporter's implicit borders/column widths changing a business's layout.
 * This node is registered only in the signature editor, never in the reply-body editor.
 */
export class SignatureElementNode extends ElementNode {
  __tag: string;
  __attrs: Attributes;

  static getType(): string { return "signature-element"; }
  static clone(node: SignatureElementNode): SignatureElementNode {
    return new SignatureElementNode(node.__tag, { ...node.__attrs }, node.__key);
  }
  constructor(tag = "p", attrs: Attributes = {}, key?: NodeKey) {
    super(key);
    this.__tag = tag;
    this.__attrs = { ...attrs };
  }
  getTag(): string { return this.getLatest().__tag; }
  getAttributes(): Attributes {
    const node = this.getLatest();
    const attrs = { ...node.__attrs };
    const style = signatureEditorStyle(node.getStyle() || attrs.style || "");
    if (style) attrs.style = style;
    else delete attrs.style;
    return attrs;
  }
  setTag(tag: string): this { this.getWritable().__tag = tag; return this; }
  setAttribute(name: string, value: string | null): this {
    const writable = this.getWritable();
    writable.__attrs = { ...writable.__attrs };
    if (value === null) delete writable.__attrs[name];
    else writable.__attrs[name] = value;
    return this;
  }
  createDOM(): HTMLElement {
    const dom = document.createElement(this.getTag());
    applyAttributes(dom, this.getAttributes());
    return dom;
  }
  updateDOM(previous: SignatureElementNode, dom: HTMLElement): boolean {
    if (previous.__tag !== this.__tag) return true;
    for (const name of Object.keys(previous.__attrs)) if (name !== "style") dom.removeAttribute(name);
    dom.removeAttribute("style");
    applyAttributes(dom, this.getAttributes());
    return false;
  }
  exportDOM(_editor: LexicalEditor): DOMExportOutput { return { element: this.createDOM() }; }
  exportJSON(): SerializedSignatureElement {
    return { ...super.exportJSON(), type: SignatureElementNode.getType(), version: 1, tag: this.getTag(), attrs: this.getAttributes() };
  }
  static importJSON(value: SerializedSignatureElement): SignatureElementNode {
    return $createSignatureElement(value.tag, value.attrs).updateFromJSON(value);
  }
  isInline(): boolean { return this.getTag() === "span" || this.getTag() === "a"; }
  canBeEmpty(): boolean { return !this.isInline(); }
  canIndent(): boolean { return false; }
  extractWithChild(): boolean { return true; }
  insertNewAfter(_selection: RangeSelection, restoreSelection = true): SignatureElementNode | null {
    if (!["p", "div", "h1", "h2", "h3", "h4", "h5", "h6", "li"].includes(this.getTag())) return null;
    const next = $createSignatureElement(this.getTag(), this.getAttributes());
    this.insertAfter(next, restoreSelection);
    return next;
  }
}

/** The live editor resolves a server-owned preview URL; HTML export always retains only CID. */
export class SignatureMediaNode extends DecoratorNode<null> {
  __tag: "img" | "hr";
  __attrs: Attributes;
  __previewUrl: string;
  static getType(): string { return "signature-media"; }
  static clone(node: SignatureMediaNode): SignatureMediaNode {
    return new SignatureMediaNode(node.__tag, { ...node.__attrs }, node.__previewUrl, node.__key);
  }
  constructor(tag: "img" | "hr" = "hr", attrs: Attributes = {}, previewUrl = "", key?: NodeKey) {
    super(key);
    this.__tag = tag;
    this.__attrs = { ...attrs };
    this.__previewUrl = previewUrl;
  }
  getTag(): "img" | "hr" { return this.getLatest().__tag; }
  getAttributes(): Attributes { return { ...this.getLatest().__attrs }; }
  setLogo(logo: SignatureEditorLogo): void {
    const node = this.getWritable();
    node.__attrs = { ...node.__attrs, src: `cid:${logo.content_id}` };
    node.__previewUrl = logo.preview_url;
  }
  /** #1452: the displayed size only. The attachment keeps the bytes that were uploaded. */
  setSize(size: { width: number; height: number }): void {
    const node = this.getWritable();
    node.__attrs = { ...node.__attrs, width: String(size.width), height: String(size.height) };
  }
  createDOM(_config?: EditorConfig): HTMLElement {
    const node = this.getLatest();
    const dom = document.createElement(node.__tag);
    const attrs = { ...node.__attrs };
    if (node.__tag === "img") {
      delete attrs.src;
      // No arbitrary remote URL from source HTML or clipboard data can become a preview fetch.
      if (node.__previewUrl.startsWith("/api/")) attrs.src = node.__previewUrl;
      dom.setAttribute("data-signature-media", node.getKey());
    }
    applyAttributes(dom, attrs);
    return dom;
  }
  updateDOM(previous: SignatureMediaNode): boolean {
    return previous.__tag !== this.__tag || previous.__previewUrl !== this.__previewUrl
      || JSON.stringify(previous.__attrs) !== JSON.stringify(this.__attrs);
  }
  exportDOM(): DOMExportOutput {
    const dom = document.createElement(this.getTag());
    applyAttributes(dom, this.getAttributes());
    return { element: dom };
  }
  exportJSON(): SerializedSignatureMedia {
    return { ...super.exportJSON(), type: SignatureMediaNode.getType(), version: 1,
      tag: this.getTag(), attrs: this.getAttributes(), previewUrl: this.getLatest().__previewUrl };
  }
  static importJSON(value: SerializedSignatureMedia): SignatureMediaNode {
    return $applyNodeReplacement(new SignatureMediaNode(value.tag, value.attrs, value.previewUrl));
  }
  decorate(): null { return null; }
  isInline(): boolean { return this.getTag() === "img"; }
  isKeyboardSelectable(): boolean { return true; }
}

export function $createSignatureElement(tag = "p", attrs: Attributes = {}): SignatureElementNode {
  const node = $applyNodeReplacement(new SignatureElementNode(tag, attrs));
  if (attrs.style) node.setStyle(attrs.style);
  return node;
}

export function $createSignatureMedia(tag: "img" | "hr", attrs: Attributes = {}, logo?: SignatureEditorLogo | null): SignatureMediaNode {
  return $applyNodeReplacement(new SignatureMediaNode(tag, attrs, logo?.preview_url ?? ""));
}

/** Import from an inert, sanitised fragment instead of the browser's default clipboard importer. */
export function $signatureNodesFromHtml(source: string, logo: SignatureEditorLogo | null): LexicalNode[] {
  const fragment = signatureEditorFragment(source, logo?.content_id ?? null);
  const visit = (node: Node, marks = { bold: false, italic: false }): LexicalNode[] => {
    if (node.nodeType === Node.TEXT_NODE) {
      const value = node.textContent ?? "";
      if (!value) return [];
      const text = $createTextNode(value);
      if (marks.bold) text.toggleFormat("bold");
      if (marks.italic) text.toggleFormat("italic");
      return [text];
    }
    if (!(node instanceof HTMLElement)) return [];
    const tag = node.tagName.toLowerCase();
    const attrs = Object.fromEntries(Array.from(node.attributes).map((attr) => [attr.name, attr.value]));
    if (tag === "br") return [$createLineBreakNode()];
    if (tag === "img" || tag === "hr") return [$createSignatureMedia(tag, attrs, logo)];
    const nextMarks = { bold: marks.bold || tag === "strong", italic: marks.italic || tag === "em" };
    const children = Array.from(node.childNodes)
      .filter((child) => !["table", "tbody", "tr"].includes(tag) || child.nodeType !== Node.TEXT_NODE || child.textContent?.trim())
      .flatMap((child) => visit(child, nextMarks));
    if ((tag === "strong" || tag === "em") && !attrs.style) return children;
    const element = $createSignatureElement(tag === "strong" || tag === "em" ? "span" : tag, attrs);
    element.append(...children);
    return [element];
  };
  const children = Array.from(fragment.childNodes)
    .filter((child) => child.nodeType !== Node.TEXT_NODE || child.textContent?.trim())
    .flatMap((child) => visit(child));
  const blocks: LexicalNode[] = [];
  let paragraph: SignatureElementNode | null = null;
  for (const child of children) {
    if (($isElementNode(child) && !child.isInline()) || (child instanceof SignatureMediaNode && !child.isInline())) {
      blocks.push(child);
      paragraph = null;
    } else {
      if (!paragraph) { paragraph = $createSignatureElement(); blocks.push(paragraph); }
      paragraph.append(child);
    }
  }
  return blocks.length ? blocks : [$createSignatureElement()];
}

export function $signatureHtml(logo: SignatureEditorLogo | null): string {
  const render = (node: LexicalNode): string => {
    if ($isTextNode(node)) {
      let text = escapeSignatureHtml(node.getTextContent()).replace(/\n/g, "<br>");
      if (node.hasFormat("bold")) text = `<strong>${text}</strong>`;
      if (node.hasFormat("italic")) text = `<em>${text}</em>`;
      const style = signatureEditorStyle(node.getStyle());
      if (style) text = `<span style="${escapeSignatureHtml(style)}">${text}</span>`;
      return text;
    }
    if ($isLineBreakNode(node)) return "<br>";
    if (node instanceof SignatureMediaNode) {
      const attrs = Object.entries(node.getAttributes()).map(([key, value]) => ` ${key}="${escapeSignatureHtml(value)}"`).join("");
      return `<${node.getTag()}${attrs}>`;
    }
    if ($isElementNode(node)) {
      const tag = node instanceof SignatureElementNode ? node.getTag() : "p";
      const attrs = node instanceof SignatureElementNode ? node.getAttributes() : { style: signatureEditorStyle(node.getStyle()) };
      const serialized = Object.entries(attrs).filter(([, value]) => value).map(([key, value]) => ` ${key}="${escapeSignatureHtml(value)}"`).join("");
      return `<${tag}${serialized}>${node.getChildren().map(render).join("")}</${tag}>`;
    }
    return "";
  };
  return normalizeSignatureEditorHtml($getRoot().getChildren().map(render).join(""), logo?.content_id ?? null);
}

export function $nearestSignatureElement(node: LexicalNode | null, tags?: readonly string[]): SignatureElementNode | null {
  for (let current = node; current; current = current.getParent()) {
    if (current instanceof SignatureElementNode && (!tags || tags.includes(current.getTag()))) return current;
  }
  return null;
}
